import { useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useFormatter, useTranslations } from 'use-intl';
import type { SetRangeFormFields } from './types';
import { parseEther } from 'viem';
import { getMaxPercentages, getMinPercentages } from './utils';
import RangeChart from './RangeChart';
import {
  Address,
  Collection,
  MAX_TICK,
  MIN_TICK,
  feeTierToPercentage,
} from 'nftx.js';
import { useNetwork } from '@nftx/react';
import { VaultHeaderFloorPrice } from '@/components/views/CreateVault/elements';
import Price from './inputs/Price';
import PriceInEth from './inputs/PriceInEth';
import PricePercentage from './inputs/PricePercentage';
import InfiniteRange from './inputs/InfiniteRange';
import Amount from './inputs/Amount';
import Explainer from './Explainer';
import { PositionBreakdownExplainer } from './PositionBreakdownExplainer';
import { getWethAddress } from '@/domain/ticks/helpers';

type Form = UseFormReturn<SetRangeFormFields>;

export interface SetRangeFormProps {
  poolAddress: Address | undefined;
  liquidity: bigint;
  tokenAddress: Address;
  symbol: string;
  feeTier: string;
  poolType: 'pair' | 'eth' | 'nft';
  vaultId?: string;
  amountMax?: bigint;
  ethMax?: bigint;
  currentTick?: string;
  icon?: string;
  control: Form['control'];
  errors: Form['formState']['errors'];
  setValue: Form['setValue'];
  isNewVault: boolean;
  isNewPool: boolean;
  using: 'vtoken' | 'nft';
  canEditRange?: boolean;
  collection: Collection | undefined;
  setDisabled: (v: boolean) => void;
  onPriceChange: (v: string) => void;
  onTickLowerChange: (v: number) => void;
  onTickUpperChange: (v: number) => void;
  onMinPriceChange: (v: string) => void;
  onMaxPriceChange: (v: string) => void;
  onMinPercentChange: (v: number) => void;
  onMaxPercentChange: (v: number) => void;
  onFullRangeChange: (v: boolean) => void;
  onEthAmountChange: (v: string) => void;
  onTokenAmountchange: (v: string) => void;
  setAmountController: (v: 'ethAmount' | 'tokenAmount') => void;
  isVToken0: boolean;
}

export const SetRangeForm = ({
  liquidity,
  poolAddress,
  tokenAddress,
  symbol,
  feeTier,
  amountMax,
  ethMax,
  currentTick = '',
  vaultId,
  icon,
  control,
  setValue,
  isNewVault,
  isNewPool,
  poolType,
  using,
  canEditRange = true,
  setDisabled,
  onPriceChange,
  onTickLowerChange,
  onTickUpperChange,
  onMinPriceChange,
  onMaxPriceChange,
  onMinPercentChange,
  onMaxPercentChange,
  onFullRangeChange,
  onEthAmountChange,
  onTokenAmountchange,
  collection,
  setAmountController,
  isVToken0,
}: SetRangeFormProps) => {
  const network = useNetwork();
  // formatting
  const t = useTranslations('CreateLiquidityPositionForms.SetRange');
  const { number } = useFormatter();
  // type of pool
  const isEthOnly = poolType === 'eth';
  const isNftOnly = poolType === 'nft';
  // details around calculating pricing
  const minPercentages = getMinPercentages(poolType);
  const maxPercentages = getMaxPercentages(poolType);

  const [showTicks, setShowTicks] = useState(false);

  // watch for when the actual form tick values are updated - source of truth
  const {
    tickLower = MIN_TICK,
    tickUpper = MAX_TICK,
    fullRange = false,
    liquidityAmount = '0',
    maxPriceInEth,
    minPriceInEth,
    tick = '0',
  } = useWatch({
    control,
  });

  // Used to build the deposit tokens
  const pair = [
    {
      key: 'tokenAmount',
      symbol,
      id: tokenAddress,
      name: '',
      max: amountMax,
    },
    {
      key: 'ethAmount',
      id: getWethAddress(network),
      symbol: 'ETH',
      name: 'Ethereum',
      max: ethMax,
    },
  ] as const;

  // used to build the range start/end selection
  const range = [
    {
      key: 'minPriceInEth' as const,
      percentageKey: 'minPercent' as const,
      tickKey: 'tickLower' as const,
      percentages: minPercentages,
    },
    {
      key: 'maxPriceInEth' as const,
      percentageKey: 'maxPercent' as const,
      tickKey: 'tickUpper' as const,
      percentages: maxPercentages,
    },
  ];

  const feePercentage = feeTierToPercentage(Number(feeTier));

  return (
    <div className="space-y-4">
      <header>
        <hgroup className="flex items-center">
          <h1 className="font-medium mr-4">{t('title')}</h1>
          <span className="w-1 h-1 rounded-full block bg-gray-500 mr-4" />
          <h2 className="text-gray-500 font-medium text-sm">
            {t.rich(`pool.${poolType}`, {
              symbol,
              fee: number(feePercentage, {
                style: 'percent',
                maximumFractionDigits: 2,
              }),
            })}
          </h2>
          <VaultHeaderFloorPrice floorPrice={collection?.floorPrice} />
        </hgroup>
      </header>
      <Price
        collection={collection}
        control={control}
        currentTick={currentTick}
        isNewPool={isNewPool}
        isNewVault={isNewVault}
        onPriceChange={onPriceChange}
        setDisabled={setDisabled}
        setValue={setValue}
        showTicks={showTicks}
        symbol={symbol}
      />

      {!isNewVault && !isNewPool && (
        <RangeChart
          feeTier={feeTier ? Number(feeTier) : 0}
          currentTick={currentTick}
          tickLower={BigInt(tickLower || '0')}
          tickUpper={BigInt(tickUpper || '0')}
          // price={price}
          fullRange={fullRange}
          isEthOnly={isEthOnly}
          isNftOnly={isNftOnly}
          inputTokens={[...pair]}
          liquidity={liquidity}
          poolAddress={poolAddress}
          userLiquidity={parseEther(liquidityAmount || '0')}
        />
      )}

      <section>
        <div
          className={`mb-2 transition-opacity grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4 ${
            fullRange ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          {range.map((x) => (
            <div key={x.key}>
              <PriceInEth
                canEditRange={canEditRange}
                control={control}
                feeTier={feeTier}
                name={x.key}
                onMaxPriceChange={onMaxPriceChange}
                onMinPriceChange={onMinPriceChange}
                onTickLowerChange={onTickLowerChange}
                onTickUpperChange={onTickUpperChange}
                poolType={poolType}
                setDisabled={setDisabled}
                setValue={setValue}
                showTicks={showTicks}
                symbol={symbol}
                tickKey={x.tickKey}
              />
              <PricePercentage
                canEditRange={canEditRange}
                control={control}
                name={x.percentageKey}
                onMaxPercentChange={onMaxPercentChange}
                onMinPercentChange={onMinPercentChange}
                percentages={x.percentages}
                poolType={poolType}
                priceInEthKey={x.key}
                setDisabled={setDisabled}
                tick={tick}
                tickKey={x.tickKey}
                isVToken0={isVToken0}
              />
            </div>
          ))}
          <PositionBreakdownExplainer
            isFullRange={fullRange}
            min={minPriceInEth}
            max={maxPriceInEth}
            symbol={symbol}
          />
        </div>
        <InfiniteRange
          canEditRange={canEditRange}
          control={control}
          onFullRangeChange={onFullRangeChange}
          poolType={poolType}
          setValue={setValue}
          setDisabled={setDisabled}
        />
      </section>
      <section>
        <h4 className="font-medium mb-2">{t('depositTokens')}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {pair.map((x) => {
            return (
              <Amount
                key={x.key}
                control={control}
                icon={icon}
                max={x.max}
                name={x.key}
                onEthAmountChange={onEthAmountChange}
                onTokenAmountchange={onTokenAmountchange}
                poolType={poolType}
                setAmountController={setAmountController}
                setDisabled={setDisabled}
                symbol={x.symbol}
                vaultId={vaultId}
                using={using}
              />
            );
          })}
        </div>
      </section>
      <Explainer
        control={control}
        poolType={poolType}
        setShowTicks={setShowTicks}
        showTicks={showTicks}
        using={using}
        amountMax={amountMax}
      />
    </div>
  );
};

export default SetRangeForm;
